/**
 * Polyfill de `Promise.withResolvers` (EU-696 — preview de PDF no mobile).
 *
 * O `pdfjs-dist` (>= 4, hoje 5.4.x) que o `react-pdf` fixa chama
 * `Promise.withResolvers()` CRU (sem `typeof`/fallback) no caminho de CARGA do
 * documento — na main thread (`MessageHandler.sendWithPromise`, usado no
 * primeiro `GetDocRequest` do `getDocument`) e dentro do worker. O método é
 * baseline-2024: só existe em Safari 17.4+, Chrome 119+ e Firefox 121+. Em
 * mobile mais antigo (iOS < 17.4, Android WebView / navegadores in-app antigos)
 * ele NÃO existe, então o `getDocument` lança
 * `TypeError: Promise.withResolvers is not a function`, o `<Document>` dispara
 * `onLoadError` e cai no fallback "Não foi possível exibir o PDF aqui." — o bug
 * "funciona no desktop, falha no mobile".
 *
 * Feature-detected: NO-OP quando o browser já tem o método (não sobrescreve a
 * implementação nativa). Precisa rodar ANTES do `react-pdf`/`pdfjs` inicializar
 * — por isso é importado como PRIMEIRA linha do `pdf-viewer.tsx` (imports ES
 * são avaliados na ordem em que aparecem no código).
 */
type PromiseWithResolvers = <T>() => {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
};

export function installPromiseWithResolvers(): void {
  const ctor = Promise as unknown as { withResolvers?: PromiseWithResolvers };
  if (typeof ctor.withResolvers === 'function') return;
  ctor.withResolvers = function withResolvers<T>() {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

// Efeito de import: instala assim que o módulo é avaliado (antes do react-pdf).
installPromiseWithResolvers();
